import type { IConfig, IToggle } from '@unleash/proxy-client-react'

import { getUnleashContext } from './context'

const url = import.meta.env?.VITE_APP_UNLEASH_URL ?? ''
const clientKey = import.meta.env?.VITE_APP_UNLEASH_CLIENT_KEY ?? ''

/**
 * The Unleash environment context value.
 *
 * account-ui tags its build env as `account-development` /
 * `account-production` — the `account-` prefix keeps its ClickUp support
 * tickets isolated from the ebs platform (see src/features/tickets), so
 * VITE_APP_ENV must keep that value. Unleash's environments are the bare names,
 * so strip the prefix here. The actual environment scoping is enforced by the
 * env-scoped client key regardless; this only sets the context field.
 */
export const toUnleashEnvironment = (appEnv: string | undefined): string =>
  (appEnv ?? 'development').replace(/^account-/, '')

const environment = toUnleashEnvironment(import.meta.env?.VITE_APP_ENV)

/**
 * True only when both the Unleash frontend-API URL and client key are set.
 * When false, the client is started with refresh + metrics disabled and serves
 * the bootstrap defaults with zero network traffic (local dev / CI without a
 * token). Behavior then matches an Unleash outage.
 */
export const isUnleashConfigured = Boolean(url && clientKey)

/**
 * Build the Unleash frontend-client config for the app's React root. `appName`
 * tags metrics; `bootstrap` (owned by the app) seeds the flags it uses and is
 * the outage fallback. `context` carries the `subdomain`/`tenant` properties so
 * multi-tenant strategy constraints can run server-side.
 */
export const createUnleashConfig = (
  appName: string,
  bootstrap: IToggle[] = []
): IConfig => ({
  url: url || 'https://unleash.invalid/api/frontend',
  clientKey: clientKey || 'unconfigured',
  appName,
  environment,
  context: getUnleashContext(),
  refreshInterval: 30,
  bootstrap,
  bootstrapOverride: true,
  disableRefresh: !isUnleashConfigured,
  disableMetrics: !isUnleashConfigured
})
